import { GRAVITATIONAL_CONSTANT } from "@/lib/tool-kit/physics-fields/constants";
import {
  formatEcho,
  formatSig,
  type NumberFormatter,
} from "@/lib/tool-kit/physics-fields/format";
import type {
  FieldSolution,
  FieldTarget,
  ForceSolution,
  ForceTarget,
} from "@/lib/tool-kit/physics-fields/gravitation";

/**
 * The equation, the substitution and the answer for each unknown, in SI units and
 * monospace-friendly text. Symbols are not translated.
 */

export function forceWorking(
  target: ForceTarget,
  s: ForceSolution,
  fmt: NumberFormatter,
  sig: number
): string[] {
  const e = (v: number) => formatEcho(fmt, v);
  const G = e(GRAVITATIONAL_CONSTANT);
  const answer = (v: number, unit: string) =>
    `${formatSig(fmt, v, sig)} ${unit}`;
  switch (target) {
    case "m1":
      return [
        "m₁ = F r² / (G m₂)",
        `m₁ = ${e(s.force)} × (${e(s.distance)})² / (${G} × ${e(s.m2)})`,
        `m₁ = ${answer(s.m1, "kg")}`,
      ];
    case "m2":
      return [
        "m₂ = F r² / (G m₁)",
        `m₂ = ${e(s.force)} × (${e(s.distance)})² / (${G} × ${e(s.m1)})`,
        `m₂ = ${answer(s.m2, "kg")}`,
      ];
    case "distance":
      return [
        "r = √(G m₁ m₂ / F)",
        `r = √(${G} × ${e(s.m1)} × ${e(s.m2)} / ${e(s.force)})`,
        `r = ${answer(s.distance, "m")}`,
      ];
    default:
      return [
        "F = G m₁ m₂ / r²",
        `F = ${G} × ${e(s.m1)} × ${e(s.m2)} / (${e(s.distance)})²`,
        `F = ${answer(s.force, "N")}`,
      ];
  }
}

export function fieldWorking(
  target: FieldTarget,
  s: FieldSolution,
  fmt: NumberFormatter,
  sig: number
): string[] {
  const e = (v: number) => formatEcho(fmt, v);
  const G = e(GRAVITATIONAL_CONSTANT);
  const answer = (v: number, unit: string) =>
    `${formatSig(fmt, v, sig)} ${unit}`;
  const distance = `${e(s.radius)} + ${e(s.altitude)}`;
  switch (target) {
    case "mass":
      return [
        "M = g (R + h)² / G",
        `M = ${e(s.gravity)} × (${distance})² / ${G}`,
        `M = ${answer(s.mass, "kg")}`,
      ];
    case "altitude":
      return [
        "h = √(G M / g) − R",
        `h = √(${G} × ${e(s.mass)} / ${e(s.gravity)}) − ${e(s.radius)}`,
        `h = ${answer(s.altitude, "m")}`,
      ];
    default:
      return [
        "g = G M / (R + h)²",
        `g = ${G} × ${e(s.mass)} / (${distance})²`,
        `g = ${answer(s.gravity, "m/s²")}`,
      ];
  }
}
